'use client';

import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

export type CloseButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'type'
>;

/**
 * Design-system close/dismiss button. Icon-only round-corner button for
 * dismissing modals, banners, and other closable surfaces. Owns the standard
 * treatment (padding, radius, icon size, --text-tertiary color, hover
 * opacity, focus-visible ring, motion-reduce transition, disabled state) so
 * every callsite gets the same close affordance without redeclaring the
 * classes. Consumed by BaseModal and WelcomeBanner today.
 *
 * Provide an `aria-label` describing the action (e.g. "Close modal",
 * "Dismiss"); defaults to "Close" if omitted.
 */
export function CloseButton({
  className,
  disabled,
  'aria-label': ariaLabel = 'Close',
  ...props
}: CloseButtonProps) {
  return (
    <button
      {...props}
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        'p-2 rounded-lg flex-shrink-0',
        'hover:opacity-70 transition-opacity',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'motion-reduce:transition-none',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      style={{ color: 'var(--text-tertiary)' }}
    >
      <X className="w-5 h-5" aria-hidden="true" />
    </button>
  );
}
